"use client";

import { useEffect } from "react";
import { isTrustedMessageSource } from "../message-source";
import { useWidgetEnv } from "../use-widget-env";
import { useWidgetInstance } from "../widget-instance-context";

/** Longer than any real JWT: a bound so a hostile frame cannot park a huge string in memory. */
const MAX_PREVIEW_TOKEN_LENGTH = 4096;

/**
 * Connects this widget's preview-token state to its embed (BE 062):
 *
 *  - outbound: a rejected token is delivered to the embed's request channel (`env`);
 *  - inbound (iframe only): the token and every replacement arrive as
 *    `wexio:preview-token:v1 { token }` from the framing page. In-DOM embeds get the token from the
 *    React prop / `preview-token` attribute, never from a message.
 *
 * The message is validated like every host message: the sender must be the framing window
 * (`isTrustedMessageSource`), the shape must be exact, and it is honoured ONLY for an embed opened
 * as a preview, so a page cannot flip an ordinary widget into the preview transport. Frame origins
 * cannot be pinned (customer pages are arbitrary); the token is worthless without the BE verifying
 * it. It is never logged, stored or put in a URL.
 */
export function usePreviewBridge(): void {
  const env = useWidgetEnv();
  const { preview } = useWidgetInstance();

  useEffect(() => {
    preview.onRequest = (reason) => env.onPreviewTokenRequest?.(reason);
    return () => {
      preview.onRequest = null;
    };
  }, [preview, env]);

  useEffect(() => {
    if (env.kind !== "iframe" || typeof window === "undefined") return;
    const onMessage = (event: MessageEvent) => {
      if (!preview.isExpected) return;
      if (!isTrustedMessageSource(event, env.kind)) return;
      const data = event.data as { type?: unknown; token?: unknown } | null;
      if (!data || typeof data !== "object" || data.type !== "wexio:preview-token:v1") return;
      const token = data.token;
      if (typeof token !== "string" || token.length === 0) return;
      if (token.length > MAX_PREVIEW_TOKEN_LENGTH) return;
      preview.setToken(token);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [env.kind, preview]);
}
